/**
 * Product Bundle Static - Cart API JavaScript
 * assets/product-bundle-static.js
 * Uses the exact cart API calls that the theme uses
 */

class ProductBundleStatic {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.initSliders();
    this.bindThemeEditorEvents();
  }

  bindEvents() {
    // Use event delegation for add to cart buttons
    document.addEventListener('click', (event) => {
      // Check if click is on the action part OR anywhere in the button
      const actionSection = event.target.closest('.bundle-split-button__action');
      const button = event.target.closest('.bundle-split-button');

      // Only trigger if we clicked the button (and not just the pricing side)
      if (!button) return;

      // Prevent default immediately when button is clicked
      event.preventDefault();
      event.stopPropagation();

      // If we clicked the pricing side specifically, ignore it
      const pricingSection = event.target.closest('.bundle-split-button__price');
      if (pricingSection && !actionSection) return;

      this.handleKitAddToCart(button);
    }, true); // Use capture phase to ensure we catch it first

    // Use event delegation for slider dots
    document.addEventListener('click', (event) => {
      const dot = event.target.closest('.bundle-slider-dot');
      if (!dot) return;

      const container = dot.closest('.bundle-slider-container');
      if (!container) return;

      const dotIndex = Array.from(dot.parentElement.children).indexOf(dot);
      this.goToSlide(container, dotIndex);
    });
  }

  async handleKitAddToCart(button) {
    const variantId = button.dataset.kitVariantId;
    const kitHandle = button.dataset.kitHandle;

    if (!variantId) {
      console.error('No variant ID found for kit:', kitHandle);
      return;
    }

    console.log('Adding kit to cart:', { variantId, kitHandle });

    // Store original text BEFORE changing to loading state
    const textSpan = button.querySelector('.btn-text');
    if (!button.dataset.originalText && textSpan) {
      button.dataset.originalText = textSpan.textContent;
    }

    // Show loading state
    this.setLoadingState(button, true);

    try {
      // Use fetch to add to cart (same as theme)
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1
            }
          ]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      console.log('Kit added successfully:', result);
      
      // Show success feedback
      this.setSuccessState(button);
      
      // Trigger cart update events (same as theme)
      this.publishCartUpdate();
      
      // Open cart drawer (same as theme)
      this.openCartDrawer();

    } catch (error) {
      console.error('Error adding kit to cart:', error);
      this.setErrorState(button);
    }
  }

  setLoadingState(button, loading) {
    const textSpan = button.querySelector('.btn-text');
    const spinner = button.querySelector('.loading__spinner');
    
    if (loading) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      if (textSpan) textSpan.textContent = 'Adding...';
      if (spinner) spinner.classList.remove('hidden');
    } else {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  setSuccessState(button) {
    const textSpan = button.querySelector('.btn-text');
    const iconSpan = button.querySelector('.btn-icon');

    this.setLoadingState(button, false);

    if (textSpan) textSpan.textContent = 'Added!';
    if (iconSpan) {
      iconSpan.style.display = 'inline';
      iconSpan.textContent = '✓';
    }

    // Reset after 2 seconds
    setTimeout(() => {
      if (textSpan) textSpan.textContent = button.dataset.originalText || 'Add';
      if (iconSpan) iconSpan.style.display = 'none';
    }, 2000);
  }

  setErrorState(button) {
    const textSpan = button.querySelector('.btn-text');

    this.setLoadingState(button, false);

    if (textSpan) textSpan.textContent = 'Error';

    // Reset after 2 seconds
    setTimeout(() => {
      if (textSpan) textSpan.textContent = button.dataset.originalText || 'Add';
    }, 2000);
  }

  publishCartUpdate() {
    // Trigger the same events that the theme uses
    document.dispatchEvent(new CustomEvent('cart:updated'));
    
    // Try other common cart events
    window.dispatchEvent && window.dispatchEvent(new CustomEvent('cart:updated'));
    document.body.dispatchEvent && document.body.dispatchEvent(new CustomEvent('cart:updated'));
    
    // Fetch updated cart and trigger refresh
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        // Dispatch with cart data
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
      })
      .catch(console.error);
  }

  openCartDrawer() {
    console.log('Attempting to open cart drawer...');

    // Method 1: Use the theme's cart-drawer custom element (primary method for Shopify Dawn theme)
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      console.log('Opening cart drawer via custom element');
      cartDrawer.open();
      return;
    }

    // Method 2: Simulate click on cart icon (this triggers the theme's built-in handler)
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (cartIcon) {
      console.log('Opening cart drawer via cart icon click');
      cartIcon.click();
      return;
    }

    // Method 3: Fallback - try to manually open drawer
    if (cartDrawer) {
      console.log('Opening cart drawer manually via classes');
      cartDrawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      return;
    }

    console.warn('Could not find cart drawer element');
  }

  initSliders() {
    const sliders = document.querySelectorAll('.bundle-slider-container');
    
    sliders.forEach(slider => {
      const track = slider.querySelector('.bundle-slider-track');
      const dots = slider.querySelectorAll('.bundle-slider-dot');
      
      if (!track || !dots.length) return;

      // Initialize slider state
      slider.currentSlide = 0;
      slider.totalSlides = dots.length;
      slider.autoSlideInterval = null;
      
      // Set first slide as active
      this.updateSliderDisplay(slider);
      
      // Start auto-slide if enabled
      const autoSlide = slider.dataset.autoSlide === 'true';
      if (autoSlide) {
        this.startAutoSlide(slider);
      }
      
      // Pause auto-slide on hover
      slider.addEventListener('mouseenter', () => {
        this.pauseAutoSlide(slider);
      });
      
      slider.addEventListener('mouseleave', () => {
        const autoSlide = slider.dataset.autoSlide === 'true';
        if (autoSlide) {
          this.startAutoSlide(slider);
        }
      });
    });
  }

  goToSlide(slider, slideIndex) {
    if (!slider) return;

    slider.currentSlide = slideIndex;
    this.updateSliderDisplay(slider);
    
    // Restart auto-slide
    const autoSlide = slider.dataset.autoSlide === 'true';
    if (autoSlide) {
      this.pauseAutoSlide(slider);
      this.startAutoSlide(slider);
    }
  }

  updateSliderDisplay(slider) {
    const track = slider.querySelector('.bundle-slider-track');
    const dots = slider.querySelectorAll('.bundle-slider-dot');
    
    if (!track) return;

    const translateX = -slider.currentSlide * 100;
    track.style.transform = `translateX(${translateX}%)`;
    
    // Update dots
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === slider.currentSlide);
      dot.setAttribute('aria-selected', index === slider.currentSlide ? 'true' : 'false');
    });
  }

  startAutoSlide(slider) {
    this.pauseAutoSlide(slider);
    
    const interval = parseInt(slider.dataset.slideInterval) || 5000;
    
    slider.autoSlideInterval = setInterval(() => {
      const nextSlide = (slider.currentSlide + 1) % slider.totalSlides;
      this.goToSlide(slider, nextSlide);
    }, interval);
  }

  pauseAutoSlide(slider) {
    if (slider.autoSlideInterval) {
      clearInterval(slider.autoSlideInterval);
      slider.autoSlideInterval = null;
    }
  }

  // Theme editor event handlers
  handleBlockSelect(event) {
    const block = event.target;
    const slider = block.querySelector('.bundle-slider-container');
    
    if (slider) {
      this.pauseAutoSlide(slider);
    }
  }

  handleBlockDeselect(event) {
    const block = event.target;
    const slider = block.querySelector('.bundle-slider-container');
    
    if (slider && slider.dataset.autoSlide === 'true') {
      this.startAutoSlide(slider);
    }
  }

  bindThemeEditorEvents() {
    if (typeof Shopify === 'undefined' || !Shopify.designMode) return;
    
    document.addEventListener('shopify:block:select', (event) => {
      this.handleBlockSelect(event);
    });
    
    document.addEventListener('shopify:block:deselect', (event) => {
      this.handleBlockDeselect(event);
    });
    
    document.addEventListener('shopify:section:load', () => {
      this.init();
    });
    
    document.addEventListener('shopify:section:unload', () => {
      // Clean up sliders
      const sliders = document.querySelectorAll('.bundle-slider-container');
      sliders.forEach(slider => {
        this.pauseAutoSlide(slider);
      });
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductBundleStatic();
  });
} else {
  new ProductBundleStatic();
}

// Export for manual initialization if needed
window.ProductBundleStatic = ProductBundleStatic;